import { calcStartPaymentDateAndLimits } from '@/_models';
import { type TGlobalDataModel, type TGlobalDataModelAuditLog, dummyCreditCard } from '@/_models';
import { withHookMoveToLink, Routes, useNav, useUrlParams } from '@/_navigation';
import { dateTime } from '@/_shared';

import { sanitizeCourseIds } from './sanitize-ids';
import { isValidLeadSummaryCampus } from './use-global-data-validation';
import type { TUseUpdateCache } from './use-update-cache';

export function useInit(data: TGlobalDataModel, updateCache: TUseUpdateCache) {
  const goto = useNav();
  const { isReviseMode, paramLeadId, paramSummaryId, paramCampusId } = useUrlParams();
  const { updateGlobalData, overrideGlobalData } = updateCache;
  const { investment: localInvestment } = data || ({} as TGlobalDataModel);

  const initLeadData = ({
    serverLeadData,
    planPrepCourseIds = [],
    planAddonCourseIds = [],
    planOdtCourseIds = [],
    planFundamentalCourseIds = [],
    isAutomatedRedirectionAllowed,
  }: {
    serverLeadData: string;
    planPrepCourseIds: string[];
    planAddonCourseIds: string[];
    planOdtCourseIds: string[];
    planFundamentalCourseIds: string[];
    isAutomatedRedirectionAllowed: boolean;
  }) => {
    // new user always make new local data
    // new user coming from investment should not initialize local data
    if (!serverLeadData || isReviseMode) {
      // no local data found so initialize
      return updateGlobalData(
        {
          investment: {
            ...localInvestment,
            selectedPrepCourseIds: planPrepCourseIds,
            selectedAddonCourseIds: planAddonCourseIds,
            selectedOdtCourseIds: planOdtCourseIds,
            selectedFundamentalCourseIds: planFundamentalCourseIds,
          },
        } as TGlobalDataModel,
        true
      );
    }

    const nServerLeadData = (
      typeof serverLeadData === 'string' ? JSON.parse(serverLeadData) : serverLeadData
    ) as TGlobalDataModel;

    // legacy code handling
    const lsSelectedPrepCourseIds =
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      nServerLeadData?.investment?.selectedPrepCourseIds ?? nServerLeadData?.investment?.selectedAtpCourses;
    const lsSelectedAddonCourseIds =
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      nServerLeadData?.investment?.selectedAddonCourseIds ?? nServerLeadData?.investment?.selectedAddonCourses;

    const lsSelectedOdtCourseIds = nServerLeadData?.investment?.selectedOdtCourseIds ?? [];
    const lsSelectedFundamentalCourseIds = nServerLeadData?.investment?.selectedFundamentalCourseIds ?? [];

    let moveToStoredLink = '';
    if (
      !nServerLeadData.leadId ||
      nServerLeadData?.leadId !== paramLeadId ||
      !isValidLeadSummaryCampus({
        paramLeadId,
        paramCampusId,
        paramSummaryId,
        localLeadId: nServerLeadData?.leadId,
        localCampusId: nServerLeadData?.campusId,
        localSummaryId: nServerLeadData?.summaryId,
      }) ||
      !nServerLeadData?.investment
    ) {
      // console.log('Resetting Investment plan');
      return updateGlobalData(
        {
          investment: {
            ...localInvestment,
            selectedPrepCourseIds: planPrepCourseIds,
            selectedAddonCourseIds: planAddonCourseIds,
            selectedOdtCourseIds: planOdtCourseIds,
            selectedFundamentalCourseIds: planFundamentalCourseIds,
          },
        } as TGlobalDataModel,
        true
      );
    }
    // console.log('Resuming Plan');
    const sanitized = sanitizeCourseIds({
      planPrepCourseIds,
      planAddonCourseIds,
      planOdtCourseIds,
      planFundamentalCourseIds,

      storedAtpCourseIds: lsSelectedPrepCourseIds,
      storedOptionalCourseIds: lsSelectedAddonCourseIds,
      storedOdtCourseIds: lsSelectedOdtCourseIds,
      storedFundamentalCourseIds: lsSelectedFundamentalCourseIds,
    });

    moveToStoredLink = nServerLeadData.link;

    // TODO in future check may required
    if (!nServerLeadData?.paymentOptions?.recurringPaymentStartDate) {
      const today = dateTime.now().toISOString();
      const { recurringPaymentStartDate, recurringPaymentDateLimits } = calcStartPaymentDateAndLimits(today, 'monthly');

      nServerLeadData.paymentOptions = {
        ...nServerLeadData.paymentOptions,
        recurringPaymentStartDate,
        recurringPaymentDateLimits,
        splitPayments: [],
      };

      moveToStoredLink = Routes.INVESTMENT.link as string;
    }

    if (nServerLeadData?.checkout) {
      if (typeof nServerLeadData.checkout.splitPifPayments === 'undefined') {
        nServerLeadData.checkout.splitPifPayments = [];
      }
      if (typeof nServerLeadData.checkout.backup_card === 'undefined') {
        nServerLeadData.checkout.backup_card = { ...dummyCreditCard };
      }
      if (typeof nServerLeadData.checkout.social_security_number === 'undefined') {
        nServerLeadData.checkout.social_security_number = '';
      }
      if (typeof nServerLeadData.checkout.termsReviewedByCustomer === 'undefined') {
        nServerLeadData.checkout.termsReviewedByCustomer = nServerLeadData.checkout.customerSyncCompleted;
        if (nServerLeadData.checkout.customerSyncCompleted) {
          nServerLeadData.checkout.hasReviewedTerms = nServerLeadData.checkout.customerSyncCompleted;
        }
      }
    }

    if (nServerLeadData.auditLog === undefined) {
      nServerLeadData.auditLog = {} as TGlobalDataModelAuditLog;
    }

    // !important to use this hook function with forceOverride
    overrideGlobalData(
      {
        ...nServerLeadData,
        investment: {
          ...nServerLeadData.investment,
          ...sanitized,
        },
      },
      true
    );

    if (moveToStoredLink && isAutomatedRedirectionAllowed) {
      let route = Routes.INVESTMENT;
      if (moveToStoredLink === Routes.PAYMENT_OPTIONS.link) {
        route = Routes.PAYMENT_OPTIONS;
      } else if (moveToStoredLink === Routes.CHECKOUT.link) {
        route = Routes.CHECKOUT;
      } else if (moveToStoredLink === Routes.BRIDGE_PLAN.link) {
        route = Routes.BRIDGE_PLAN;
      }

      withHookMoveToLink(goto, paramLeadId, route.link as string);
    }
    // leave it localStorage is updated

    return undefined;
  };

  return initLeadData;
}
